import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Col, Row, Form, Overlay, Popover } from "react-bootstrap";
import DatePicker from "react-datepicker";

import { withNavigation } from "../../../utils/wrapper";
import { toDateOrNull } from "../../../utils/object";
import CodeSelect from "../../../layout/select/code";
import Hamburger from "../../../layout/kebab";
import ConfirmModal from "../../../layout/modal";

function normalizeAdmission(admission = {}) {
  return {
    ...(admission || {}),
    admissionType: admission?.admissionType ?? "state_bar",
    jurisdiction: admission?.jurisdiction ?? "",
    courtName: admission?.courtName ?? "",
    barNumber: admission?.barNumber ?? "",
    admissionDate: toDateOrNull(admission?.admissionDate),
    status: admission?.status ?? "active",
    isPrimary: Boolean(admission?.isPrimary),
    meta:
      admission?.meta &&
      typeof admission.meta === "object" &&
      !Array.isArray(admission.meta)
        ? { ...admission.meta }
        : {},
  };
}

function Admission(props) {
  const {
    index,
    uuid,
    _cid,
    jurisdiction,
    courtName,
    admissionType,
    barNumber,
    admissionDate,
    status,
    isPrimary,
    meta,
    onChange,
    onDelete,
    backgroundColor,
  } = props;

  const [uuidState, setUuidState] = useState(uuid ?? null);
  const [cidState, setCidState] = useState(_cid ?? null);
  const [admissionState, setAdmissionState] = useState(() =>
    normalizeAdmission({
      jurisdiction,
      courtName,
      admissionType,
      barNumber,
      admissionDate,
      status,
      isPrimary,
      meta,
    })
  );
  const [menuState, setMenuState] = useState({
    show: false,
    target: null,
  });
  const [modalState, setModalState] = useState({
    confirm: {
      show: false,
      title: "",
      body: "",
      onConfirm: null,
    },
  });

  const normalizedFromProps = useMemo(
    () =>
      normalizeAdmission({
        jurisdiction,
        courtName,
        admissionType,
        barNumber,
        admissionDate,
        status,
        isPrimary,
        meta,
      }),
    [jurisdiction, courtName, admissionType, barNumber, admissionDate, status, isPrimary, meta]
  );

  useEffect(() => {
    setUuidState(uuid ?? null);
    setCidState(_cid ?? null);
    setAdmissionState(normalizedFromProps);
  }, [uuid, _cid, normalizedFromProps]);

  const closeHamburger = useCallback(() => {
    setMenuState({
      show: false,
      target: null,
    });
  }, []);

  const closeConfirm = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      confirm: {
        ...prev.confirm,
        show: false,
        title: "",
        body: "",
        onConfirm: null,
      },
    }));
  }, []);

  const openConfirm = useCallback(({ title, body, onConfirm }) => {
    setModalState((prev) => ({
      ...prev,
      confirm: {
        show: true,
        title,
        body,
        onConfirm,
      },
    }));
  }, []);

  const emitChange = useCallback(
    (nextValues = {}) => {
      if (!onChange) return;

      const nextAdmission = normalizeAdmission({
        ...admissionState,
        ...(nextValues || {}),
      });

      onChange({
        index,
        values: {
          uuid: uuidState,
          _cid: cidState,
          ...nextAdmission,
        },
      });
    },
    [onChange, index, uuidState, cidState, admissionState]
  );

  const updateAdmission = useCallback(
    (patch = {}) => {
      setAdmissionState((prev) => {
        const nextAdmission = normalizeAdmission({
          ...(prev || {}),
          ...(patch || {}),
        });
        emitChange(nextAdmission);
        return nextAdmission;
      });
    },
    [emitChange]
  );

  const onRequestDelete = useCallback(
    (e) => {
      e?.preventDefault?.();
      closeHamburger();

      const displayName =
        admissionState?.jurisdiction ||
        admissionState?.courtName ||
        admissionState?.barNumber ||
        `Admission #${index + 1}`;

      openConfirm({
        title: "Remove admission",
        body: `Are you sure you want to remove ${displayName}?`,
        onConfirm: () => {
          closeConfirm();
          onDelete?.({ index });
        },
      });
    },
    [
      closeHamburger,
      admissionState?.jurisdiction,
      admissionState?.courtName,
      admissionState?.barNumber,
      index,
      openConfirm,
      closeConfirm,
      onDelete,
    ]
  );

  const openHamburger = useCallback((event) => {
    event.preventDefault();
    setMenuState({
      show: true,
      target: event.currentTarget,
    });
  }, []);

  return (
    <>
      <Col
        xs="12"
        style={{ backgroundColor, padding: "20px", borderBottom: "1px solid black" }}
      >
        <Row className="g-3">
          <Col xs="11">
            <h3 className="small-title">Admission #{index + 1}</h3>
          </Col>
          <Col xs="1">
            <Hamburger variant="link" vertical={false} onClick={openHamburger} />
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="6" lg="4">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="jurisdiction"
                value={admissionState?.jurisdiction ?? ""}
                onChange={(e) => updateAdmission({ jurisdiction: e.target.value })}
              />
              <Form.Label>JURISDICTION</Form.Label>
            </div>
          </Col>

          <Col md="6" lg="4">
            <div className="mb-3 top-label">
              <CodeSelect
                codes={[
                  { value: "state_bar", description: "State Bar" },
                  { value: "federal_court", description: "Federal Court" },
                  { value: "bankruptcy_court", description: "Bankruptcy Court" },
                  { value: "tax_court", description: "Tax Court" },
                  { value: "agency", description: "Agency" },
                  { value: "other", description: "Other" },
                ]}
                value={admissionState?.admissionType ?? "state_bar"}
                onChange={(e) =>
                  updateAdmission({ admissionType: e?.value ?? "state_bar" })
                }
                placeholder=""
                name="admissionType"
              />
              <Form.Label>TYPE</Form.Label>
            </div>
          </Col>

          <Col md="6" lg="4">
            <div className="mb-3 top-label">
              <CodeSelect
                codes={[
                  { value: "active", description: "Active" },
                  { value: "inactive", description: "Inactive" },
                  { value: "resigned", description: "Resigned" },
                  { value: "retired", description: "Retired" },
                  { value: "suspended", description: "Suspended" },
                  { value: "unknown", description: "Unknown" },
                ]}
                value={admissionState?.status ?? "active"}
                onChange={(e) => updateAdmission({ status: e?.value ?? "active" })}
                placeholder=""
                name="status"
              />
              <Form.Label>STATUS</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="6" lg="4">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="courtName"
                value={admissionState?.courtName ?? ""}
                onChange={(e) => updateAdmission({ courtName: e.target.value })}
              />
              <Form.Label>COURT NAME</Form.Label>
            </div>
          </Col>

          <Col md="6" lg="4">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="barNumber"
                value={admissionState?.barNumber ?? ""}
                onChange={(e) => updateAdmission({ barNumber: e.target.value })}
              />
              <Form.Label>BAR NUMBER</Form.Label>
            </div>
          </Col>

          <Col md="6" lg="4">
            <div className="mb-3 top-label">
              <DatePicker
                isClearable
                id={`admissionDate-${index}`}
                name="admissionDate"
                className="form-control"
                selected={toDateOrNull(admissionState?.admissionDate)}
                onChange={(date) => updateAdmission({ admissionDate: date })}
              />
              <Form.Label>ADMISSION DATE</Form.Label>
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md="6" lg="4">
            <div className="mb-3 top-label">
              <Form.Check
                type="checkbox"
                id={`isPrimary-admission-${index}`}
                name="isPrimary"
                checked={Boolean(admissionState?.isPrimary)}
                onChange={(e) => updateAdmission({ isPrimary: e.target.checked })}
                label="Primary Admission"
              />
            </div>
          </Col>
        </Row>
      </Col>

      <Overlay
        target={menuState.target}
        show={menuState.show}
        placement="bottom-end"
        rootClose
        onHide={closeHamburger}
      >
        {(overlayProps) => (
          <Popover {...overlayProps}>
            <Popover.Body>
              <Row className="mb-2">
                <Col className="d-flex flex-row mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2 btn-sm small text-start"
                    onClick={onRequestDelete}
                  >
                    Remove
                  </button>
                </Col>
              </Row>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>

      <ConfirmModal
        {...modalState.confirm}
        buttons={[
          {
            caption: "ok",
            onEnabledClick: modalState.confirm.onConfirm,
            variant: "secondary",
            display: true,
            enabled: true,
          },
          {
            default: true,
            caption: "cancel",
            onEnabledClick: closeConfirm,
            variant: "primary",
            display: true,
            enabled: true,
          },
        ]}
      />
    </>
  );
}

Admission.propTypes = {
  auth: PropTypes.object,
  index: PropTypes.number,
  uuid: PropTypes.string,
  _cid: PropTypes.string,
  backgroundColor: PropTypes.string,
  jurisdiction: PropTypes.string,
  courtName: PropTypes.string,
  admissionType: PropTypes.string,
  barNumber: PropTypes.string,
  admissionDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ]),
  status: PropTypes.string,
  isPrimary: PropTypes.bool,
  meta: PropTypes.object,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
};

Admission.defaultProps = {
  index: 0,
  uuid: null,
  _cid: null,
  backgroundColor: "white",
  jurisdiction: "",
  courtName: "",
  admissionType: "state_bar",
  barNumber: "",
  admissionDate: null,
  status: "active",
  isPrimary: false,
  meta: {},
  onChange: null,
  onDelete: null,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(
  connect(mapStateToProps, null, null, { forwardRef: true })(Admission)
);